// frontend/src/pages/reports/StockoutReport.jsx
// Report 2: Projected Stockout — all items ranked by risk level

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { Button } from '@mui/material';
import PageBanner from '../../components/common/PageBanner';
import Tile from '../../components/layout/Tile';
import ReportKpiStrip from '../../components/reports/ReportKpiStrip';
import { ItemFilterBar, useItemFilter } from '../../features/item-filter';
import { riskLevelColumn } from '../../components/common/riskLevelColumn';
import { RISK_LEVELS, RISK_CHIP_COLOR, riskLabelKey } from '../../components/common/riskLevels';
import { useSsrmGrid, buildExportQuery } from '../../hooks/useSsrmGrid';
import { useIsShopify, itemIdentityColumns } from '../../components/grid/productIdentity';
import { daysOfCoverColDef } from '../../components/grid/daysOfCoverColumn';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { useProductGrouping } from '../../components/grid/productGrouping';
import { useReportFilters } from '../../hooks/useReportFilters';
import { useCreateRecommendationsFromStockout } from '../../hooks/useReportActionMutations';
import { translateRequestError } from '../../queries/api';
import { showToast } from '../../components/common/appToastBus';
import { useTabs } from '../../tabs/useTabs';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, copyOnlyContextMenuItems } from '../../utils/gridDefaults';
import { scopeHeaderCheckboxToPage } from '../../utils/gridSelection';
import GridExportButton from '../../components/common/GridExportButton';
import { buildPinnedTotalsRow, wrapColumnsForPinnedTotals } from '../../utils/pinnedTotals';
import '../../styles/pages.css';
import { GridEmptyState } from '../../components/grid/NoRowsOverlay';
import { GridPage, GridViewport } from '../../components/grid/GridPage';

const RED_AT_ZERO_OR_BELOW = {
  'ag-cell-attention-error': (p) => p.value != null && p.value <= 0,
};

function StockoutReport() {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber } = useFormatters();
  const defaultHidden = useMemo(() => ['shopify_product_title', 'committed', 'on_hand', 'lead_time'], []);
  // Group variants by product (productGrouping): the switch, the product
  // tree, the row id and the saved column state, one preference for every
  // report. A product row carries the report's totals restricted to that
  // product and the report's own measure over it; only those columns say
  // `sortable: true`, since the server orders the products by them.
  const grouping = useProductGrouping({
    gridId: 'report_stockout',
    preferenceKey: 'reports_grouped',
    defaultHidden,
    leafIdFields: ['item_id', 'location_id'],
  });
  const { isGrouped, isGroupRow, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef }),
    [grouping.defaultColDef],
  );
  const gridRef = useRef(null);

  const itemFilter = useItemFilter();
  const { location, setLocation, productCategory, setProductCategory } = useReportFilters('stockout');

  // One KPI card per risk level. Its filter routes through the backend
  // `risk_filter` query param so pinned-bottom totals reflect the filtered
  // subset, not the entire tracked catalogue.
  const [kpiFilter, setKpiFilter] = useState(null);
  const handleKpiClick = useCallback((key) => {
    setKpiFilter(prev => (prev === key ? null : key));
  }, []);

  const params = useMemo(() => ({
    location_code: location, product_category_id: productCategory?.id ?? null, ...itemFilter.params,
    risk_filter: kpiFilter ?? undefined,
  }), [location, productCategory, itemFilter.params, kpiFilter]);

  const [chrome, setChrome] = useState({
    summary: null, currency: null, totals: null,
  });

  const onData = useCallback((data) => {
    setChrome({
      summary: data.summary ?? null,
      currency: data.currency ?? null,
      totals: data.totals ?? null,
    });
  }, []);

  const { onGridReady, onGridPreDestroyed, loading, error, pagination } = useSsrmGrid({
    endpoint: '/api/reports/stockout-items/data/',
    params,
    onData,
    gridRef,
  });

  const { summary, currency, totals } = chrome;

  const { openTab } = useTabs();

  // Server-side export of the full filtered dataset (respects KPI risk filter).
  const exportProps = {
    endpoint: '/api/reports/stockout-items/export/',
    filename: 'projected_stockout',
    getParams: useCallback(
      (scope) => (scope === 'all' ? '' : buildExportQuery(params)),
      [params],
    ),
  };

  const data = useMemo(() => ({ summary, currency }), [summary, currency]);

  // Row selection for "Create Recommendation"
  const [selectedCount, setSelectedCount] = useState(0);
  const createRecommendationsMutation = useCreateRecommendationsFromStockout();
  const creating = createRecommendationsMutation.isPending;

  const handleSelectionChanged = useCallback((e) => {
    // The header checkbox produces an SSRM select-all RULE, under which
    // getSelectedRows() returns [] and this button would never appear. The
    // recommendation payload carries each row's on-hand / incoming / safety
    // stock / days of cover, so it can only cover rows the grid has loaded —
    // confine the select-all to the page in front of the merchant. The
    // conversion is synchronous, so the count below reads the real selection.
    scopeHeaderCheckboxToPage(e.api);
    setSelectedCount(e.api.getSelectedRows().length);
  }, []);

  const handleCreateRecommendation = useCallback(async () => {
    const api = gridRef.current;
    if (!api) return;

    const selectedRows = api.getSelectedRows();
    if (!selectedRows.length) return;

    // The selection only. The server plans these item-locations with the PO
    // engine; no figure from this grid is copied onto a recommendation line.
    const items = selectedRows.map(r => ({ item_location_id: r.item_location_id }));

    try {
      const result = await createRecommendationsMutation.mutateAsync({ items });
      api.deselectAll();
      showToast(
        [
          t('reports.stockout.actions.createRecSuccess', { count: result.added_lines }),
          result.no_need.length > 0
            && t('reports.stockout.actions.noNeed', { count: result.no_need.length }),
          ...Object.entries(result.excluded).map(([reason, codes]) => t('reports.stockout.actions.excluded', {
            count: codes.length,
            reason: t(`purchaseRecommendations.itemsExcluded.reasons.${reason}`, { count: codes.length }),
          })),
          result.skipped_already_pending > 0
            && t('reports.stockout.actions.skippedAlreadyPending', { count: result.skipped_already_pending }),
        ].filter(Boolean).join(' '),
        'success',
        { label: t('nav.createPurchaseOrders'), onClick: () => openTab('/purchase-recommendations') },
      );
    } catch (err) {
      console.error('createRecommendationsFromStockout:', err.message);
      showToast(translateRequestError(err, t, 'reports.stockout.actions.createRecFailed'), 'error');
    }
  }, [createRecommendationsMutation, t, openTab]);

  const kpiItems = useMemo(() => {
    if (!data?.summary) return [];
    const s = data.summary;
    // The chip's own label and the column legend's explanation, so a card
    // reads exactly like the level it filters to.
    return RISK_LEVELS.map((level) => ({
      label: t(`reports.stockout.${riskLabelKey(level)}`),
      value: s[level],
      key: level,
      tooltip: t(`reports.stockout.riskLegend.${level}`),
      highlight: s[level] > 0 ? RISK_CHIP_COLOR[level] : undefined,
    }));
  }, [data, t]);

  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('item_code'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  const columnDefs = useMemo(() => wrapColumnsForPinnedTotals([
    // ?ordering=risk_level orders by risk, then days of cover: the report's own order.
    { ...riskLevelColumn({ headerName: t('reports.stockout.columns.riskLevel') }), initialSort: 'asc', sortable: true },
    ...groupedColumns([variantProductColumn()]),
    ...(isGrouped ? [] : itemIdentityColumns(isShopify, {
      productHeader: t('reports.columns.product'),
      codeHeader: t('reports.columns.itemCode'),
      nameHeader: t('reports.columns.itemName'),
    })),
    { field: 'category_label', headerName: t('reports.columns.productCategory'), minWidth: 100 },
    { field: 'location_name', headerName: t('reports.columns.location'), minWidth: 100 },
    { field: 'supplier_name', headerName: t('reports.stockout.columns.supplier') },
    { field: 'abc_class', headerName: t('reports.columns.abcClass'), type: 'centeredColumn' },
    {
      field: 'available', sortable: true, headerName: t('reports.stockout.columns.available'), type: 'numberColumn',
      cellClassRules: RED_AT_ZERO_OR_BELOW,
    },
    { field: 'committed', sortable: true, headerName: t('reports.stockout.columns.committed'), type: 'numberColumn' },
    { field: 'on_hand', sortable: true, headerName: t('reports.stockout.columns.onHand'), type: 'numberColumn' },
    { field: 'incoming', sortable: true, headerName: t('reports.stockout.columns.inTransit'), type: 'numberColumn', headerTooltip: t('reports.stockout.tooltips.inTransit') },
    {
      field: 'forecast_daily', headerName: t('reports.stockout.columns.forecastDaily'), type: 'decimalColumn',
      headerTooltip: t('common.tooltips.dailyDemand'),
      valueFormatter: (p) => p.value > 0 ? formatNumber(p.value, 1) : '—',
    },
    {
      field: 'forecast_30d', sortable: true, headerName: t('reports.stockout.columns.forecast30d'), type: 'numberColumn',
      valueFormatter: (p) => p.value > 0 ? formatNumber(p.value, 0) : '—',
    },
    {
      ...daysOfCoverColDef({ t, formatNumber }),
      cellClassRules: RED_AT_ZERO_OR_BELOW,
    },
    { field: 'stockout_date', headerName: t('reports.stockout.columns.stockoutDate'), type: 'dateColumn' },
    { field: 'pending_rec_qty', sortable: true, headerName: t('reports.stockout.columns.pendingRec'), type: 'numberColumn' },
    { field: 'target_stock', sortable: true, headerName: t('reports.stockout.columns.targetStock'), type: 'numberColumn', headerTooltip: t('common.tooltips.targetStock') },
    {
      field: 'lead_time', headerName: t('reports.stockout.columns.leadTime'), type: 'numberColumn',
      headerTooltip: t('common.tooltips.leadTime'),
      // An item with no default supplier has no lead time. numberColumn's
      // own null branch renders '0', which reads as same-day delivery.
      valueFormatter: (p) => (p.value == null ? '—' : formatNumber(p.value, 0)),
    },
  ]), [isShopify, isGrouped, groupedColumns, variantProductColumn, t, formatNumber]);


  return (
    <div className="page-container">
      <ReportKpiStrip
        title={t('reports.stockout.kpi.title')}
        items={kpiItems}
        loading={loading}
        activeKey={kpiFilter}
        onCardClick={handleKpiClick}
        storageKey="stockout"
      />

      <PageBanner severity="error" messages={[error && translateRequestError(error, t, 'common.fetchError')]} />

      <div className="tiles-row">
        <Tile width="full" title={t('nav.reportStockout')} headerActions={<GridExportButton {...exportProps} />}>
          <GridPage>
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: productCategory, set: setProductCategory, show: true }}
              locationFilter={{ selected: location, set: setLocation }}
              actions={<>
                {grouping.toggle}
                {selectedCount > 0 ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreateRecommendation}
                    loading={creating}
                  >
                    {t('reports.stockout.actions.createRecommendation', { count: selectedCount })}
                  </Button>
                ) : null}
              </>}
            />
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  title={t('reports.stockout.emptyTitle')}
                  message={t('reports.stockout.emptyState')}
                  filtersActive={location !== 'all' || !!productCategory || !!itemFilter.filter || !!kpiFilter}
                >
                  <AgGridReact
                    theme={gridTheme} columnDefs={columnDefs} columnTypes={columnTypes}
                    loading={loading}
                    {...defaultGridProps} defaultColDef={gridDefaultColDef}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    rowSelection={{
                      mode: 'multiRow',
                      checkboxes: true,
                      headerCheckbox: true,
                      // A product row is a sum, not something to act on: variant rows only.
                      isRowSelectable: (node) => (
                        !node.rowPinned && !node.data?.__isPinnedTotal && !isGroupRow(node.data)
                      ),
                      hideDisabledCheckboxes: true,
                    }}
                    onSelectionChanged={handleSelectionChanged}
                    {...grouping.gridProps}
                    rowModelType="serverSide"
                    {...pagination}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    pinnedBottomRowData={pinnedBottomRowData}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default StockoutReport;
